import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Search from "src/components/molecules/WorkRequestListViewContainer/components/Search";
import DataTable from "src/components/Table";
import TableRow from "src/components/Table/TableRow";
import { Cell } from "src/components/Table/Cell";
import { LOCATIONS_TABLE_HEADERS } from "src/pages/ClientDetails/Locations/constants";
import { DBLocation } from "src/types";
import { formatTimezoneToShort } from "src/utils/time";

interface Client {
  id: string;
  name: string;
  customerId?: string;
}

interface ClientSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (clientId: string, locationId: string, subLocationId?: string) => void;
  clients: Client[];
  locations: DBLocation[];
  currentClientId?: string;
  currentLocationId?: string;
}

export const ClientSelectionModal: React.FC<ClientSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  clients,
  locations,
  currentClientId = "",
  currentLocationId = "",
}) => {
  const [step, setStep] = useState<"client" | "location" | "sublocation">("client");
  const [selectedClientId, setSelectedClientId] = useState(currentClientId);
  const [selectedLocationId, setSelectedLocationId] = useState(currentLocationId);
  const [selectedSubLocationId, setSelectedSubLocationId] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const selectedClient = useMemo(() => clients.find((c) => c.id === selectedClientId), [selectedClientId, clients]);

  const locationOptions = useMemo(() => {
    const selectedClientKeys = [
      String(selectedClientId || ""),
      String(selectedClient?.id || ""),
      String(selectedClient?.customerId || ""),
    ].filter(Boolean);

    if (!selectedClientKeys.length) {
      return [];
    }

    const selectedClientKeySet = new Set(selectedClientKeys);

    return locations.filter((loc) => {
      const locationClientId = String((loc as any)?.clientId || (loc as any)?.clientid || "");
      return selectedClientKeySet.has(locationClientId);
    });
  }, [locations, selectedClientId, selectedClient]);

  const effectiveSearch = (searchValue || appliedSearch).trim().toLowerCase();

  const displayedClients = useMemo(() => {
    if (!effectiveSearch) return clients;

    return clients.filter((client) => {
      const id = String(client.id || "").toLowerCase();
      const name = String(client.name || "").toLowerCase();
      return id.includes(effectiveSearch) || name.includes(effectiveSearch);
    });
  }, [clients, effectiveSearch]);

  const displayedLocationOptions = useMemo(() => {
    if (!effectiveSearch) return locationOptions;

    return locationOptions.filter((location) => {
      const id = String(location.id || "").toLowerCase();
      const label = (location.label || "").toLowerCase();
      const address = (location.address || "").toLowerCase();
      const timezone = (location.timezone || "").toLowerCase();
      const supervisor = String((location as any).assignedSupervisorName || "").toLowerCase();

      return (
        id.includes(effectiveSearch) ||
        label.includes(effectiveSearch) ||
        address.includes(effectiveSearch) ||
        timezone.includes(effectiveSearch) ||
        supervisor.includes(effectiveSearch)
      );
    });
  }, [locationOptions, effectiveSearch]);

  const selectedLocation = useMemo(
    () => locationOptions.find((loc) => loc.id === selectedLocationId),
    [selectedLocationId, locationOptions],
  );

  const subLocations = useMemo(() => {
    if (!selectedLocation || !selectedLocation["sub-locations"]) return [];
    return selectedLocation["sub-locations"];
  }, [selectedLocation]);

  const displayedSubLocations = useMemo(() => {
    if (!effectiveSearch) return subLocations;

    return subLocations.filter((subLoc) => {
      const id = String(subLoc.id || "").toLowerCase();
      const name = String(subLoc.name || "").toLowerCase();
      return id.includes(effectiveSearch) || name.includes(effectiveSearch);
    });
  }, [subLocations, effectiveSearch]);

  useEffect(() => {
    if (!isOpen) return;

    setStep("client");
    setSelectedClientId(String(currentClientId || ""));
    setSelectedLocationId(String(currentLocationId || ""));
    setSelectedSubLocationId("");
    setSearchValue("");
    setAppliedSearch("");
  }, [isOpen, currentClientId, currentLocationId]);

  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(String(clientId));
    setSelectedLocationId("");
    setSelectedSubLocationId("");
    setSearchValue("");
    setAppliedSearch("");
    setStep("location");
  };

  const handleLocationSelect = (locationId: string) => {
    const normalizedLocationId = String(locationId || "");
    setSelectedLocationId(normalizedLocationId);
    setSelectedSubLocationId("");
    const pickedLocation = locationOptions.find(
      (location) => String(location.id || location.label || "") === normalizedLocationId,
    );
    const pickedSubLocations = pickedLocation?.["sub-locations"] || [];

    if (pickedSubLocations.length > 0) {
      setStep("sublocation");
    } else {
      onConfirm(String(selectedClientId || ""), normalizedLocationId);
      handleReset();
      onClose();
    }
  };

  const handleSubLocationSelect = (subLocationId: string) => {
    const normalizedSubLocationId = String(subLocationId || "");
    setSelectedSubLocationId(normalizedSubLocationId);
    onConfirm(String(selectedClientId || ""), String(selectedLocationId || ""), normalizedSubLocationId);
    handleReset();
    onClose();
  };

  const handleSkipSubLocation = () => {
    onConfirm(String(selectedClientId || ""), String(selectedLocationId || ""));
    handleReset();
    onClose();
  };

  const handleReset = () => {
    setStep("client");
    setSelectedClientId(currentClientId);
    setSelectedLocationId(currentLocationId);
    setSelectedSubLocationId("");
    setSearchValue("");
    setAppliedSearch("");
  };

  const handleBack = () => {
    if (step === "location") {
      setStep("client");
      setSelectedLocationId("");
      setSelectedSubLocationId("");
      setSearchValue("");
      setAppliedSearch("");
    } else if (step === "sublocation") {
      setStep("location");
      setSelectedSubLocationId("");
      setSearchValue("");
      setAppliedSearch("");
    }
  };

  if (!isOpen) return null;

  const closeAndReset = () => {
    handleReset();
    onClose();
  };

  return (
    <>
      <ModalOverlay onClick={closeAndReset} />
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            {step === "client" && "Select Client"}
            {step === "location" && `Select Location for ${selectedClient?.name}`}
            {step === "sublocation" && `Select Sub-Location (Optional)`}
          </ModalTitle>
          <ModalCloseButton onClick={closeAndReset}>×</ModalCloseButton>
        </ModalHeader>

        {step === "client" && (
          <ModalBody>
            <SearchWrapper>
              <Search
                value={searchValue}
                applied={!!appliedSearch}
                onChange={setSearchValue}
                onClickApply={() => setAppliedSearch(searchValue.trim())}
                onClickCancel={() => setSearchValue(appliedSearch)}
                onClickClear={() => {
                  setSearchValue("");
                  setAppliedSearch("");
                }}
              />
            </SearchWrapper>

            <ListGrid>
              {displayedClients.length ? (
                displayedClients.map((client) => (
                  <ListItem
                    key={client.id}
                    $selected={selectedClientId === client.id}
                    onClick={() => handleClientSelect(client.id)}
                  >
                    <ListItemText>{client.name}</ListItemText>
                    <ListItemChevron>›</ListItemChevron>
                  </ListItem>
                ))
              ) : (
                <EmptyState>No clients match your search.</EmptyState>
              )}
            </ListGrid>
          </ModalBody>
        )}

        {step === "location" && (
          <ModalBody>
            <SearchWrapper>
              <Search
                value={searchValue}
                applied={!!appliedSearch}
                onChange={setSearchValue}
                onClickApply={() => setAppliedSearch(searchValue.trim())}
                onClickCancel={() => setSearchValue(appliedSearch)}
                onClickClear={() => {
                  setSearchValue("");
                  setAppliedSearch("");
                }}
              />
            </SearchWrapper>

            <TableWrapper>
              {displayedLocationOptions.length > 0 ? (
                <DataTable headers={LOCATIONS_TABLE_HEADERS}>
                  {displayedLocationOptions.map((location) => {
                    const locationId = String(location.id || location.label || "");
                    const isSelected = String(selectedLocationId) === String(location.id);
                    const supervisorName = (location as any).assignedSupervisorName || "-";
                    const timezoneValue = location.timezone ? formatTimezoneToShort(location.timezone) : "N/A";

                    return (
                      <TableRow
                        key={locationId}
                        $clickable={true}
                        onClick={() => handleLocationSelect(location.id || location.label)}
                        $bgColor={isSelected ? "#f3f4f6" : "#ffffff"}
                      >
                        <Cell showMark highlight={effectiveSearch}>
                          {locationId}
                        </Cell>
                        <Cell showMark highlight={effectiveSearch}>
                          {location.label || "N/A"}
                        </Cell>
                        <Cell showMark highlight={effectiveSearch}>
                          {location.address || "N/A"}
                        </Cell>
                        <Cell showMark highlight={effectiveSearch}>
                          {timezoneValue}
                        </Cell>
                        <Cell showMark highlight={effectiveSearch}>
                          {supervisorName}
                        </Cell>
                      </TableRow>
                    );
                  })}
                </DataTable>
              ) : (
                <EmptyState>No locations match your search.</EmptyState>
              )}
            </TableWrapper>
          </ModalBody>
        )}

        {step === "sublocation" && (
          <ModalBody>
            <SearchWrapper>
              <Search
                value={searchValue}
                applied={!!appliedSearch}
                onChange={setSearchValue}
                onClickApply={() => setAppliedSearch(searchValue.trim())}
                onClickCancel={() => setSearchValue(appliedSearch)}
                onClickClear={() => {
                  setSearchValue("");
                  setAppliedSearch("");
                }}
              />
            </SearchWrapper>

            <ListGrid>
              {displayedSubLocations.length ? (
                displayedSubLocations.map((subLoc) => (
                  <ListItem
                    key={subLoc.id || subLoc.name}
                    $selected={selectedSubLocationId === (subLoc.id || subLoc.name)}
                    onClick={() => handleSubLocationSelect(subLoc.id || subLoc.name)}
                  >
                    <ListItemText>{subLoc.name}</ListItemText>
                    <ListItemChevron>›</ListItemChevron>
                  </ListItem>
                ))
              ) : (
                <EmptyState>No sub-locations match your search.</EmptyState>
              )}
            </ListGrid>
            <SkipSubLocationButton onClick={handleSkipSubLocation}>Skip – use location only</SkipSubLocationButton>
          </ModalBody>
        )}

        <ModalFooter>
          {step !== "client" && (
            <FooterButton $variant="secondary" onClick={handleBack}>
              Back
            </FooterButton>
          )}
          <FooterButton $variant="secondary" onClick={closeAndReset}>
            Cancel
          </FooterButton>
        </ModalFooter>
      </ModalContainer>
    </>
  );
};

// ─── Styled Components ───────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #ffffff;
  border: 1px solid #dde2e7;
  border-radius: 4px;
  width: min(1200px, 96vw);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #dde2e7;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2a37;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: #6f7782;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #1f2a37;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const SearchWrapper = styled.div`
  margin-bottom: 12px;

  > div {
    width: 100%;
  }
`;

const TableWrapper = styled.div`
  border: 1px solid #dde2e7;
`;

const EmptyState = styled.div`
  padding: 28px;
  text-align: center;
  color: #6f7782;
  font-size: 13px;
`;

const ListGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ListItem = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${({ $selected }) => ($selected ? "#1f2a37" : "#dde2e7")};
  background: ${({ $selected }) => ($selected ? "#f3f4f6" : "#ffffff")};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    background: #f9fafb;
    border-color: #1f2a37;
  }
`;

const ListItemText = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #1f2a37;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

// const ListItemMeta = styled.span`
//   font-size: 11px;
//   color: #6f7782;
//   font-weight: 300;
// `;

const ListItemChevron = styled.span`
  font-size: 18px;
  color: #c9d2dc;
  margin-left: 12px;
  flex-shrink: 0;
`;

const SkipSubLocationButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  margin-top: 12px;
  font-size: 12px;
  font-weight: 300;
  color: #6f7782;
  background: transparent;
  border: 1px solid #dde2e7;
  border-radius: 2px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    color: #1f2a37;
    border-color: #1f2a37;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #dde2e7;
  justify-content: flex-end;
`;

const FooterButton = styled.button<{ $variant?: "primary" | "secondary" }>`
  padding: 10px 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border: 1px solid #c9d2dc;
  background: ${({ $variant }) => ($variant === "primary" ? "#1f2a37" : "#ffffff")};
  color: ${({ $variant }) => ($variant === "primary" ? "#ffffff" : "#1f2a37")};
  cursor: pointer;
  border-radius: 2px;
  transition: all 0.15s ease;

  &:hover {
    border-color: #1f2a37;
    background: ${({ $variant }) => ($variant === "primary" ? "#1f2a37" : "#f9fafb")};
  }
`;
